import { useEffect, useRef, useState } from "react";
import { CheckCircle, Circle, DownloadSimple, FileArrowDown, Trash, UploadSimple, Warning } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { uploadInChunks, downloadBlob, formatBytes, STATUS_LABEL, STATUS_CHIP } from "@/lib/bootcampFiles";

const ACCEPT = ".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg,.zip,.mp4,.mov";

export default function Bootcamps() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(1);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/bootcamps`);
      setData(data.bootcamps);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not load bootcamps");
    }
  };
  useEffect(() => { load(); }, []);

  const bc = data?.find((b) => b.number === active);
  return (
    <div className="min-h-screen" style={{ background: "var(--background)" }}>
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="bootcamps-page">
        <div>
          <p className="label-uppercase" style={{ color: "var(--muted)" }}>6 Months Mentorship</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold" style={{ color: "var(--foreground)" }}>Bootcamps</h1>
          <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>Post-session tasks, workbooks and your submissions for each bootcamp.</p>
        </div>
        <div className="status-filter" role="tablist" data-testid="bootcamp-tabs">
          {[1, 2, 3, 4].map((n) => (
            <button key={n} role="tab" className={`status-pill ${active === n ? "is-active" : ""}`} onClick={() => setActive(n)} data-testid={`bootcamp-tab-${n}`}>
              Bootcamp {n}
            </button>
          ))}
        </div>
        {error && <p className="error-banner" data-testid="bootcamps-error">{error}</p>}
        {!data && !error && <div className="py-10 flex justify-center"><span className="spinner" /></div>}
        {bc && <BootcampPanel bc={bc} onChanged={load} />}
      </main>
    </div>
  );
}

function BootcampPanel({ bc, onChanged }) {
  const ticked = bc.tasks.filter((t) => bc.ticked_task_ids.includes(t.id)).length;
  const completed = bc.status === "completed";
  return (
    <div className="space-y-6" data-testid={`bootcamp-panel-${bc.number}`}>
      <div className="grid-frame p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: "var(--foreground)" }}>{bc.title}</h2>
          {bc.published && bc.tasks.length > 0 && (
            <p className="text-sm" style={{ color: "var(--muted)" }} data-testid="bootcamp-progress-line">
              {ticked} of {bc.tasks.length} tasks marked complete by your coach
            </p>
          )}
        </div>
        <span className={STATUS_CHIP[bc.status]} data-testid="bootcamp-status">{STATUS_LABEL[bc.status]}</span>
      </div>

      {!bc.published ? (
        <div className="grid-frame p-8 text-center" data-testid="bootcamp-coming-soon" style={{ color: "var(--muted)" }}>Content coming soon.</div>
      ) : (
        <>
          {bc.notes && (
            <section className="grid-frame p-5">
              <p className="label-uppercase mb-2" style={{ color: "var(--muted)" }}>From the speaker</p>
              <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--foreground)" }} data-testid="bootcamp-notes">{bc.notes}</p>
            </section>
          )}
          {bc.tasks.length > 0 && (
            <section className="grid-frame p-5">
              <p className="label-uppercase mb-3" style={{ color: "var(--muted)" }}>Post-bootcamp tasks</p>
              <ol className="space-y-3" data-testid="bootcamp-tasks">
                {bc.tasks.map((t, i) => {
                  const done = bc.ticked_task_ids.includes(t.id);
                  return (
                    <li key={t.id} className="flex gap-3" data-testid={`bootcamp-task-${t.id}`}>
                      <span style={{ color: done ? "var(--lime)" : "var(--muted)" }}>{done ? <CheckCircle size={20} weight="fill" /> : <Circle size={20} />}</span>
                      <div>
                        <p className="font-medium text-sm" style={{ color: "var(--foreground)" }}>{i + 1}. {t.title}</p>
                        {t.instructions && <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--muted)" }}>{t.instructions}</p>}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </section>
          )}
          {bc.resources.length > 0 && (
            <section className="grid-frame p-5">
              <p className="label-uppercase mb-3" style={{ color: "var(--muted)" }}>Resources</p>
              <ul className="divide-y" style={{ borderColor: "var(--border)" }} data-testid="bootcamp-resources">
                {bc.resources.map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                    <span className="flex items-center gap-2 min-w-0" style={{ color: "var(--foreground)" }}><FileArrowDown size={16} /> <span className="truncate">{r.filename}</span> <span style={{ color: "var(--muted)" }}>· {formatBytes(r.size)}</span></span>
                    <button className="btn btn-ghost btn-sm" onClick={() => downloadBlob(`/bootcamps/${bc.number}/resources/${r.id}/download`, r.filename)} data-testid={`resource-download-${r.id}`}>
                      <DownloadSimple size={14} /> Download
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      <SubmissionsPanel bc={bc} readOnly={completed} onChanged={onChanged} />
    </div>
  );
}

function SubmissionsPanel({ bc, readOnly, onChanged }) {
  const [queue, setQueue] = useState([]);
  const [note, setNote] = useState("");
  const [taskId, setTaskId] = useState("");
  const [err, setErr] = useState("");
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const addFiles = async (files) => {
    if (readOnly || !files?.length) return;
    setErr("");
    for (const file of Array.from(files)) {
      const item = { name: file.name, size: file.size, progress: 0 };
      setQueue((q) => [...q, item]);
      try {
        await uploadInChunks(bc.number, file, {
          note, taskId: taskId || null,
          onProgress: (p) => setQueue((q) => q.map((x) => (x === item ? { ...x, progress: p } : x))),
        });
      } catch (e) {
        setErr(`${file.name}: ${formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"}`);
      } finally {
        setQueue((q) => q.filter((x) => x !== item));
      }
    }
    setNote("");
    onChanged();
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.filename}?`)) return;
    try {
      await api.delete(`/bootcamps/${bc.number}/submissions/${s.id}`);
      onChanged();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not remove file");
    }
  };

  return (
    <section
      className={`grid-frame p-5 ${drag ? "niche-dropzone-active" : ""}`}
      onDragOver={(e) => { e.preventDefault(); if (!readOnly) setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
      data-testid="bootcamp-submissions"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="label-uppercase" style={{ color: "var(--muted)" }}>Your submissions</p>
        <span className="text-xs" style={{ color: "var(--muted)" }} data-testid="bootcamp-quota">{formatBytes(bc.used_bytes)} of {formatBytes(bc.quota_bytes)} used</span>
      </div>
      {readOnly ? (
        <p className="text-sm mb-3" style={{ color: "var(--muted)" }} data-testid="bootcamp-readonly-note">This bootcamp is completed — your submissions are now read-only.</p>
      ) : (
        <div className="flex flex-wrap gap-2 items-center mb-3">
          <input className="kb-input flex-1 min-w-[180px]" placeholder="Optional note, e.g. Task 2 — pricing worksheet" value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} data-testid="submission-note-input" />
          {bc.tasks.length > 0 && (
            <select className="kb-input" value={taskId} onChange={(e) => setTaskId(e.target.value)} data-testid="submission-task-select">
              <option value="">Link to a task (optional)</option>
              {bc.tasks.map((t, i) => <option key={t.id} value={t.id}>{i + 1}. {t.title}</option>)}
            </select>
          )}
          <button type="button" className="btn btn-accent btn-sm" onClick={() => inputRef.current?.click()} data-testid="submission-upload-button"><UploadSimple size={14} weight="bold" /> Upload files</button>
          <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden" data-testid="submission-file-input" onChange={(e) => { const f = Array.from(e.target.files || []); e.target.value = ""; addFiles(f); }} />
        </div>
      )}
      {err && <p className="error-banner mb-3" data-testid="submission-error"><Warning size={14} /> {err}</p>}
      {queue.map((q, i) => (
        <div key={i} className="mb-2" data-testid="submission-uploading">
          <div className="flex justify-between text-xs" style={{ color: "var(--muted)" }}><span className="truncate">{q.name}</span><span>{q.progress}%</span></div>
          <div className="upload-bar"><div className="upload-bar-fill" style={{ width: `${q.progress}%` }} /></div>
        </div>
      ))}
      {bc.submissions.length === 0 && queue.length === 0 ? (
        <p className="text-sm" style={{ color: "var(--muted)" }} data-testid="submissions-empty">No files yet. Drag and drop or use Upload files — PDF, Word, PowerPoint, Excel, images, ZIP, MP4/MOV.</p>
      ) : (
        <ul className="divide-y" style={{ borderColor: "var(--border)" }} data-testid="submissions-list">
          {bc.submissions.map((s) => (
            <li key={s.id} className="flex items-center justify-between gap-3 py-2 text-sm" data-testid={`submission-${s.id}`}>
              <div className="min-w-0">
                <p className="truncate" style={{ color: "var(--foreground)" }}>{s.filename} <span style={{ color: "var(--muted)" }}>· {formatBytes(s.size)}</span></p>
                <p className="text-xs" style={{ color: "var(--muted)" }}>{new Date(s.uploaded_at).toLocaleString()}{s.note ? ` · ${s.note}` : ""}{s.task_id ? ` · ${bc.tasks.find((t) => t.id === s.task_id)?.title || "task"}` : ""}</p>
              </div>
              <div className="flex gap-1 flex-none">
                <button className="btn btn-ghost btn-sm" onClick={() => downloadBlob(`/bootcamps/${bc.number}/submissions/${s.id}/download`, s.filename)} data-testid={`submission-download-${s.id}`}><DownloadSimple size={14} /></button>
                {!readOnly && <button className="btn btn-ghost btn-sm" onClick={() => remove(s)} data-testid={`submission-remove-${s.id}`}><Trash size={14} /></button>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
